import { useSyncExternalStore } from 'react'

/**
 * The maintenance page (#131, 1.114.0): whether the server just said
 * "maintenance" -- one place for the whole app.
 *
 * **Set by an answer, never guessed.** The server answers 503 with
 * `maintenance: true` to whoever does not pass (`Maintenance.php`);
 * `api/client.ts` reports it here, and `App` shows the page instead of the
 * routes. Whoever passes (the supervisor, an author while only readers are
 * locked) never gets that answer and never sees the page.
 *
 * **Taken back by a login or by the page itself.** A login may be the one
 * of someone who passes, and the next answer decides again; the page asks
 * `/api/health` and reloads once the server lets this device through.
 */
export interface Maintenance {
  since: string | null
  until: string | null
  text: string
  scope: 'all' | 'readers'
}

let current: Maintenance | null = null
const listeners = new Set<() => void>()

function notify(): void {
  for (const listener of listeners) listener()
}

export function maintenanceSeen(state: Maintenance): void {
  current = state
  notify()
}

export function maintenanceOver(): void {
  if (current === null) return
  current = null
  notify()
}

export function maintenanceNow(): Maintenance | null {
  return current
}

export function useMaintenance(): Maintenance | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current,
  )
}

/** The state out of a 503's body -- or null when it is another 503. */
export function maintenanceFrom(body: unknown): Maintenance | null {
  if (typeof body !== 'object' || body === null) return null
  const fields = body as Record<string, unknown>
  if (fields.maintenance !== true) return null
  return {
    since: typeof fields.since === 'string' ? fields.since : null,
    until: typeof fields.until === 'string' ? fields.until : null,
    text: typeof fields.text === 'string' ? fields.text : '',
    scope: fields.scope === 'readers' ? 'readers' : 'all',
  }
}
